import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { fetchEntries } from '@/api/entries';
import { qk } from '@/lib/query';
import { addDays, parseDay, toDayString } from '@/lib/format';
import { useClients } from '@/hooks/useReference';
import { useInvoiceSettings } from '@/hooks/useSettings';
import type { Paginated, TimeEntry } from '@/types';

/** Monday of the week holding `day`: the phone's week starts on Monday. */
export function mondayOf(day: string) {
  const d = parseDay(day);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return toDayString(d);
}

export function weekDays(day: string) {
  const start = mondayOf(day);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

/**
 * Every entry of mine in the week, all pages merged into one paginated-shaped
 * cache. The key sits under `qk.daycardsRoot`, so the shared optimistic
 * create/update/delete hooks patch it and their invalidations refetch it.
 */
export function useWeekEntries(day: string) {
  const from = mondayOf(day);
  const to = addDays(from, 6);
  return useQuery({
    queryKey: [...qk.daycardsRoot, 'mobile-week', from, to] as const,
    queryFn: async ({ signal }): Promise<Paginated<TimeEntry>> => {
      const query = (page: number) =>
        fetchEntries(
          { page, filters: { dateFrom: from, dateTo: to, scope: 'me' }, sort: { field: null, order: 'desc' } },
          signal
        );
      const first = await query(1);
      if (first.totalPages <= 1) return first;
      const rest = await Promise.all(
        Array.from({ length: first.totalPages - 1 }, (_, i) => query(i + 2))
      );
      const items = [first, ...rest].flatMap((p) => p.items);
      return { ...first, items, totalCount: items.length, totalPages: 1 };
    },
    placeholderData: keepPreviousData
  });
}

/** Value of an entry: its own rate, else the client's, else the team default. */
export function useEntryValue() {
  const clients = useClients().data ?? [];
  const fallback = useInvoiceSettings().data?.defaultHourlyRate ?? 0;
  return (e: Pick<TimeEntry, 'time' | 'hourlyRate' | 'clientId'>) =>
    e.time * (e.hourlyRate ?? clients.find((c) => c.id === e.clientId)?.hourlyRate ?? fallback);
}

/** Hours with one decimal, as the canvas shows them ("1.6", "4.1 h"). */
export function oneDecimal(h: number) {
  return (Math.round(h * 10) / 10).toFixed(1);
}
